import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  memo,
  useCallback,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/src/components/ui/dialog";
import { Button } from "@/src/components/ui/button";
import { Input } from "@/src/components/ui/input";
import { useToast } from "@/src/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Post } from "@/src/api";
import { UserInstalledGame } from "@/src/types/user";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/src/components/ui/select";

interface AddCoinsModalData {
  userId: string;
  installedGames: UserInstalledGame[];
}

export interface AddCoinsModalRef {
  openModal: () => void;
}

const BLOCKED_CHARECTERS = ["e", "E", " "];

const AddCoinsModal = forwardRef<AddCoinsModalRef, AddCoinsModalData>(
  ({ userId, installedGames }, ref) => {
    const dialogTriggerRef = useRef<HTMLButtonElement>(null);

    const openModal = useCallback(() => {
      dialogTriggerRef.current?.click();
    }, []);

    const closeModal = useCallback(() => {
      dialogTriggerRef.current?.click();
      setSelectedGame("");
      setNote("");
      if (coinInputRef.current) {
        coinInputRef.current.value = "";
      }
    }, []);

    useImperativeHandle(ref, () => ({
      openModal,
    }));

    const coinInputRef = useRef<HTMLInputElement>(null);
    const [selectedGame, setSelectedGame] = React.useState<string>("");
    const [note, setNote] = React.useState<string>("");
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const handleSuccess = useCallback(() => {
      queryClient.invalidateQueries({
        queryKey: ["user", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["reward-history", userId],
      });
      closeModal();
      toast({
        title: "Success",
        description: "Coins added successfully",
        variant: "success",
      });
      coinInputRef.current.value = "";
    }, [queryClient, userId, toast, closeModal, coinInputRef]);

    const addCoinsMutation = useMutation({
      mutationFn: (data: {
        userId: number;
        coins: number;
        packageName: string | null;
        note: string;
      }) =>
        Post("/support/user/add-coins", data).catch((e) => {
          console.log(e);
          throw e;
        }),
      onSuccess: handleSuccess,
      onError: (e) => {
        console.log(e);
        toast({
          title: "Failed to add coins",
          variant: "destructive",
        });
      },
    });

    const validateCoinInput = useCallback(
      async (value: string) => {
        if (!value) {
          toast({
            title: "Please enter a coin amount",
            variant: "default",
          });
          return false;
        }

        if (BLOCKED_CHARECTERS.some((char) => value.includes(char))) {
          toast({
            title: "Please enter a valid number",
            variant: "default",
          });
          return false;
        }

        return true;
      },
      [toast],
    );

    const handleAddCoins = useCallback(
      (_userId: string, _value: string) => {
        addCoinsMutation.mutate({
          userId: parseInt(_userId),
          coins: parseInt(_value),
          packageName: selectedGame || null,
          note: note.trim(),
        });
      },
      [addCoinsMutation, selectedGame, note],
    );

    return (
      <Dialog>
        <DialogTrigger className="hidden" ref={dialogTriggerRef} />
        <DialogContent
          aria-describedby="add-coins-modal"
          aria-description="add coins modal"
        >
          <DialogHeader>
            <DialogTitle>Add Coins</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-8 items-center gap-4">
              <div className="col-span-3">
                <Select onValueChange={setSelectedGame} value={selectedGame}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a game" />
                  </SelectTrigger>
                  <SelectContent>
                    {installedGames.map((game) => (
                      <SelectItem
                        key={game.packageName}
                        value={game.packageName}
                      >
                        {game.name.trim()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                id="coinAmount"
                ref={coinInputRef}
                placeholder="Enter coin amount"
                type={"number"}
                className="col-span-3"
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    const value = e.currentTarget.value;
                    const isValid = await validateCoinInput(value);
                    if (!isValid) return;

                    handleAddCoins(userId, value);
                  }
                }}
                onBlur={(e) => {
                  const value = e.currentTarget.value;

                  if (BLOCKED_CHARECTERS.some((char) => value.includes(char))) {
                    e.currentTarget.value = "";
                  }

                  if (value.charAt(0) === "0" && value.length > 1) {
                    e.currentTarget.value = value.slice(1);
                  }
                }}
              />
              <Button
                className="col-span-2"
                onClick={async () => {
                  const value = coinInputRef.current.value;
                  const isValid = await validateCoinInput(value);
                  if (!isValid) return;

                  handleAddCoins(userId, value);
                }}
                disabled={addCoinsMutation.isPending}
                isLoading={addCoinsMutation.isPending}
              >
                Add Coins
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-2">
              <Input
                placeholder="Add a note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={255}
              />
              <div className="flex w-full justify-between gap-2 items-start">
                <p className="text-xs text-muted-foreground">
                  This note is for internal reference only and won't be visible
                  to users
                </p>
                <span className={"text-xs text-muted-foreground"}>
                  {note.length}/255
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  },
);

AddCoinsModal.displayName = "AddCoinsModal";

export default memo(AddCoinsModal);
